import { Headphones, Clock, KeyRound, Cctv, Tv, TrainFront, UtensilsCrossed } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { SectionHead } from "@/components/site/Contact";

export function Overview() {
  const { t } = useLang();
  const stats = [["s1", "s1l"], ["s2", "s2l"], ["s3", "s3l"]];
  return (
    <section id="overview" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="overview-section">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHead eyebrow={t("overview.eyebrow")} title={t("overview.title")} />
          <p className="text-base text-slate-300 md:text-lg">{t("overview.p1")}</p>
          <p className="mt-4 text-base text-slate-400 md:text-lg">{t("overview.p2")}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 self-end lg:col-span-5 lg:grid-cols-1">
          {stats.map(([v, l]) => (
            <div key={v} className="panel p-5 lg:flex lg:items-baseline lg:gap-4">
              <div className="font-display text-4xl text-white sm:text-5xl">{t(`overview.${v}`)}</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{t(`overview.${l}`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const AMEN = [
  ["gear", Headphones, "lg:col-span-6"], ["access", Clock, "lg:col-span-3"], ["keycard", KeyRound, "lg:col-span-3"],
  ["cams", Cctv, "lg:col-span-3"], ["lounge", Tv, "lg:col-span-3"], ["metro", TrainFront, "lg:col-span-3"], ["food", UtensilsCrossed, "lg:col-span-3"],
];

export function Amenities() {
  const { t } = useLang();
  return (
    <section id="amenities" className="border-y border-white/5 bg-[#0c0d11]" data-testid="amenities-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <SectionHead eyebrow={t("amen.eyebrow")} title={t("amen.title")} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
          {AMEN.map(([k, Icon, span], i) => (
            <div key={k} data-testid={`amenity-${k}`} className={`panel panel-hover reveal p-6 sm:p-8 ${span}`} style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-500/40 bg-red-950/40 text-red-400"><Icon size={20} /></div>
              <h3 className="text-xl font-semibold text-white">{t(`amen.${k}`)}</h3>
              <p className="mt-2 text-sm text-slate-400">{t(`amen.${k}D`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks({ embedded = false }) {
  const { t } = useLang();
  const body = (
    <>
      <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((n) => (
          <li key={n} className="panel relative p-6" data-testid={`how-step-${n}`}>
            <span className="font-display text-5xl text-white/10">0{n}</span>
            <p className="mt-2 text-sm text-slate-200 md:text-base">{t(`how.p${n}`)}</p>
          </li>
        ))}
      </ol>
      <ul className="mt-4 space-y-2 text-sm text-slate-400">
        {[1, 2, 3].map((n) => <li key={n} data-testid={`how-note-${n}`} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-600" />{t(`how.n${n}`)}</li>)}
      </ul>
    </>
  );
  if (embedded) return <div className="mb-10" data-testid="member-how-it-works"><p className="eyebrow mb-3">{t("how.title")}</p>{body}</div>;
  return (
    <section id="how-it-works" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="how-section">
      <SectionHead eyebrow={t("how.eyebrow")} title={t("how.title")} />
      {body}
    </section>
  );
}
